<template>
    <div>
        <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
            <v-btn-toggle v-model="view" mandatory dense color="primary">
                <v-btn small value="timeline" title="Timeline"><v-icon small>mdi-chart-timeline</v-icon></v-btn>
                <v-btn small value="month" title="Month"><v-icon small>mdi-calendar-month-outline</v-icon></v-btn>
            </v-btn-toggle>
            <v-text-field
                v-model="search"
                placeholder="Search staff"
                prepend-inner-icon="mdi-magnify"
                dense outlined hide-details clearable
                style="max-width: 220px;"
            ></v-text-field>
            <!-- The staff calendar has only the viewer's section; HR's (allStaff) every section (MonthlyScheduleService::calendarSection). -->
            <v-autocomplete
                v-if="sections.length > 1"
                v-model="sectionId"
                :items="sections"
                item-text="name" item-value="id"
                placeholder="All sections"
                dense outlined hide-details clearable
                style="max-width: 260px;"
            ></v-autocomplete>
            <v-select
                v-model="typeKey"
                :items="types"
                item-text="label" item-value="key"
                placeholder="All types"
                dense outlined hide-details clearable
                style="max-width: 200px;"
            ></v-select>
            <v-spacer></v-spacer>
            <v-menu offset-y left :close-on-content-click="false">
                <template v-slot:activator="{ on, attrs }">
                    <v-badge :value="activeOptions > 0" :content="activeOptions" color="primary" overlap bordered>
                        <v-btn small text v-bind="attrs" v-on="on" :color="activeOptions ? 'primary' : undefined">
                            <v-icon small left>mdi-tune-variant</v-icon>Options
                        </v-btn>
                    </v-badge>
                </template>
                <v-card class="pa-3" width="290">
                    <div class="caption grey--text mb-1">Show</div>
                    <v-btn-toggle v-model="plannedFilter" mandatory dense color="primary" class="mb-3">
                        <v-btn small value="all">All</v-btn>
                        <v-btn small value="planned">Planned</v-btn>
                        <v-btn small value="actual">Actual leave</v-btn>
                    </v-btn-toggle>
                    <v-switch v-model="showLong" dense hide-details class="mt-0 pt-0"
                        :label="`Long leaves (over ${longDays} days)`"></v-switch>
                    <div class="caption grey--text options-hint">{{ longCount }} this month, hidden unless on</div>
                    <v-switch v-if="view === 'timeline'" v-model="onlyWithEntries" dense hide-details class="mt-3 pt-0"
                        label="Only staff with entries"></v-switch>
                </v-card>
            </v-menu>
            <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn small text v-bind="attrs" v-on="on">
                        <v-icon small left>mdi-information-outline</v-icon>Legend
                    </v-btn>
                </template>
                <v-card class="pa-3" width="270">
                    <div v-for="t in types" :key="t.key" class="legend-row">
                        <span class="swatch" :style="{ background: t.color }"></span>{{ t.label }}
                    </div>
                    <v-divider class="my-2"></v-divider>
                    <div class="legend-row"><span class="swatch swatch-planned"></span>Planned (striped / “Planned” tag)</div>
                    <div class="legend-row"><span class="swatch swatch-pending"></span>Not approved yet</div>
                    <div class="legend-row"><span class="swatch swatch-off"></span>Weekend / public holiday</div>
                    <div v-if="view === 'month'" class="legend-row"><span class="swatch swatch-blocked"></span>Holiday week (no WFH)</div>
                </v-card>
            </v-menu>
        </div>

        <v-progress-linear v-if="loading" indeterminate height="2"></v-progress-linear>

        <team-timeline
            v-if="view === 'timeline' && data"
            :year="year"
            :month="month"
            :staff="timelineStaff"
            :entries="shown"
            :type-by-key="typeByKey"
            :holidays="data.holidays"
            :title-for="entryTitle"
        />

        <div v-if="view === 'month'" class="monthly-calendar">
            <FullCalendar ref="cal" :options="calendarOptions">
                <template v-slot:eventContent="arg">
                    <div v-if="arg.event.extendedProps.kind === 'entry'" class="entry-label">
                        <v-avatar size="18" color="white" class="mr-1 flex-shrink-0">
                            <img :src="arg.event.extendedProps.profile_url" alt="">
                        </v-avatar>
                        <span class="entry-empno">{{ arg.event.extendedProps.empno }}</span>
                        <span class="entry-name">{{ arg.event.title }}</span>
                        <span v-if="arg.event.extendedProps.planned" class="entry-planned">Planned</span>
                    </div>
                </template>
            </FullCalendar>
        </div>
    </div>
</template>

<script>
// The one shared calendar for all sections: every staff member's plans —
// schedule entries (WFH, leave plans, custom types) and Leave Planner plans,
// shown as "Planned" — and their actual (approved HRMS) leave. Two views of
// the same data: the team timeline (staff × days, by section) and the month
// grid. Data, type colours, staff and holidays come from
// MonthlyScheduleService::calendar(); this only filters and draws them.
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import TeamTimeline from './TeamTimeline.vue'

export default {
    name: 'SharedCalendar',
    components: { FullCalendar, TeamTimeline },
    props: {
        year: { type: Number, required: true },
        month: { type: Number, required: true },
        mySectionId: { type: Number, default: null },
        allStaff: { type: Boolean, default: false }, // HR's calendar: every section
    },
    data() {
        return {
            data: null,
            loading: false,
            view: 'timeline',
            search: '',
            sectionId: this.mySectionId || null,
            typeKey: null,
            plannedFilter: 'all', // all | planned | actual
            showLong: false,
            onlyWithEntries: true,
            calendarOptions: {
                plugins: [dayGridPlugin],
                initialView: 'dayGridMonth',
                initialDate: this.monthStart(),
                headerToolbar: false,
                fixedWeekCount: false,
                showNonCurrentDates: false,
                firstDay: 0,
                dayMaxEvents: 4,
                height: 'auto',
                events: [],
                eventDidMount: (info) => {
                    const p = info.event.extendedProps
                    if (p.kind === 'entry') info.el.title = this.entryTitle(p)
                },
            },
        }
    },
    computed: {
        types() {
            return this.data ? this.data.types : []
        },
        typeByKey() {
            const map = {}
            this.types.forEach(t => { map[t.key] = t })
            return map
        },
        staffById() {
            const map = {}
            if (this.data) this.data.staff.forEach(s => { map[s.staff_id] = s })
            return map
        },
        sections() {
            if (!this.data) return []
            const byId = {}
            this.data.staff.forEach(s => { byId[s.section_id] = s.section })
            return Object.entries(byId)
                .map(([id, name]) => ({ id: Number(id), name }))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        },
        longDays() {
            return this.data ? this.data.long_leave_days : 35
        },
        longCount() {
            return this.data ? this.data.entries.filter(e => e.long).length : 0
        },
        // Staff matching the section and search filters, in the backend's order.
        staffShown() {
            if (!this.data) return []
            const q = (this.search || '').trim().toLowerCase()
            return this.data.staff.filter(s =>
                (!this.sectionId || s.section_id === this.sectionId)
                && (!q || `${s.empno} ${s.name}`.toLowerCase().includes(q)))
        },
        shown() {
            if (!this.data) return []
            const ids = {}
            this.staffShown.forEach(s => { ids[s.staff_id] = true })
            return this.data.entries.filter(e =>
                ids[e.staff_id] && (!this.typeKey || e.type === this.typeKey) && (this.showLong || !e.long)
                && (this.plannedFilter === 'all' || e.planned === (this.plannedFilter === 'planned')))
        },
        // Options switched on, defaults included (the Options button shows the count).
        activeOptions() {
            return [this.plannedFilter !== 'all', this.showLong, this.view === 'timeline' && this.onlyWithEntries]
                .filter(Boolean).length
        },
        // A type or planned/actual filter lists only the staff who have a match.
        timelineStaff() {
            if (!this.onlyWithEntries && !this.typeKey && this.plannedFilter === 'all') return this.staffShown
            const ids = {}
            this.shown.forEach(e => { ids[e.staff_id] = true })
            return this.staffShown.filter(s => ids[s.staff_id])
        },
    },
    watch: {
        year() { this.load() },
        month() { this.load() },
        shown() { this.draw() },
    },
    mounted() {
        this.load()
    },
    methods: {
        monthStart() {
            return `${this.year}-${String(this.month).padStart(2, '0')}-01`
        },
        load() {
            this.loading = true
            this.calendarOptions.initialDate = this.monthStart()
            this.$refs.cal && this.$refs.cal.getApi().gotoDate(this.monthStart())
            const params = { year: this.year, month: this.month }
            if (this.allStaff) params.all = 1
            this.$axios.get('/api/monthly-schedule/calendar', { params })
                .then(resp => { this.data = resp.data })
                .finally(() => this.loading = false)
        },
        draw() {
            if (!this.data) return
            const entries = this.shown.map(e => {
                const t = this.typeByKey[e.type] || { label: e.type, color: '#757575' }
                const s = this.staffById[e.staff_id] || {}
                return {
                    title: s.name,
                    start: e.from_date,
                    end: this.nextDay(e.to_date),
                    allDay: true,
                    backgroundColor: e.approved ? t.color : this.tint(t.color),
                    borderColor: 'transparent',
                    textColor: e.approved ? '#ffffff' : t.color,
                    extendedProps: { kind: 'entry', ...e, empno: s.empno, profile_url: s.profile_url },
                }
            })
            const blocked = this.data.weeks.filter(w => w.blocked).map(w => ({
                start: w.dates[0],
                end: this.nextDay(w.dates[w.dates.length - 1]),
                display: 'background',
                color: '#FFE0B2',
            }))
            this.calendarOptions.events = [...blocked, ...entries]
        },
        entryTitle(e) {
            const s = this.staffById[e.staff_id] || {}
            const t = this.typeByKey[e.type] || { label: e.type }
            const unit = s.unit && s.unit !== '-' ? ` / ${s.unit}` : ''
            return `${s.empno} ${s.name} — ${t.label}\n${this.fmtRange(e.from_date, e.to_date)}\n${s.section}${unit}\n${this.sourceText(e)}`
                + (e.approved ? '' : e.submitted ? '\n(waiting for approval)' : '\n(not approved yet)')
        },
        sourceText(e) {
            if (e.source === 'planner') return `Planned in Leave Planner (${(e.plan_status || 'draft').replace(/_/g, ' ')})`
            return e.planned ? 'Planned on this calendar' : 'Leave (approved in HRMS)'
        },
        // Not-approved entries: a light wash of the type colour.
        tint(color) {
            return /^#[0-9a-f]{6}$/i.test(color) ? `${color}26` : '#eeeeee'
        },
        fmtRange(from, to) {
            const f = s => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}` }
            return from === to ? f(from) : `${f(from)} – ${f(to)}`
        },
        nextDay(s) {
            const d = new Date(`${s}T00:00:00`)
            d.setDate(d.getDate() + 1)
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        },
    },
}
</script>

<style scoped>
.options-hint {
    margin-left: 52px;
}
.legend-row {
    display: flex;
    align-items: center;
    font-size: 13px;
    line-height: 24px;
}
.swatch {
    display: inline-block;
    flex-shrink: 0;
    width: 22px;
    height: 12px;
    border-radius: 3px;
    margin-right: 8px;
}
.swatch-planned {
    background: repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0 2px, transparent 2px 6px), #757575;
}
.swatch-pending { background: #75757526; }
.swatch-off { background: #f1f1f1; border: 1px solid #e0e0e0; }
.swatch-blocked { background: #FFE0B2; }

.monthly-calendar :deep(.fc) {
    font-size: 13px;
}
.monthly-calendar :deep(.fc-daygrid-event) {
    border: none;
}
.entry-label {
    display: flex;
    align-items: center;
    overflow: hidden;
    padding: 1px 2px;
}
.entry-empno {
    font-weight: 600;
    margin-right: 4px;
    flex-shrink: 0;
}
.entry-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.entry-planned {
    flex-shrink: 0;
    margin-left: 4px;
    padding: 0 4px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.35);
    font-size: 10px;
    font-weight: 600;
}
</style>
